import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import BrandLogo from '../common/BrandLogo';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { getRoleLabel } from '../../constants/roles';
import { sidebarNavItems } from '../../constants/navigation';

const SIDEBAR_COLLAPSED_KEY = 'gfi-sidebar-collapsed';

function readCollapsed() {
  try {
    return sessionStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export default function Sidebar() {
  const { isDark, toggleTheme } = useTheme();
  const { profile, signOut, can } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      sessionStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore quota / private mode */
    }
  }, [collapsed]);

  const displayName = profile?.full_name || 'Usuario';
  const displayRole = getRoleLabel(profile?.role);
  const showAdmin = can('view_agent_conflicts');
  const showSuperadmin = can('manage_users');

  const linkClass = ({ isActive }) =>
    `flex items-center rounded-xl transition-all duration-200 ${
      collapsed ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3'
    } ${
      isActive
        ? 'bg-[var(--text-main)]/5 text-[var(--text-main)] font-extrabold shadow-sm'
        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-base)] hover:text-[var(--text-main)]'
    }`;

  return (
    <aside
      className={`hidden md:flex flex-col bg-[var(--bg-surface)] border-r border-[var(--border-subtle)] h-screen sticky top-0 shadow-sm transition-[width] duration-300 ease-out ${
        collapsed ? 'w-[4.5rem]' : 'w-64'
      }`}
    >
      <div className={`flex items-center ${collapsed ? 'flex-col gap-3 p-3 pt-5' : 'justify-between p-6 pb-2'}`}>
        {collapsed ? (
          <button
            type="button"
            onClick={() => navigate('/app')}
            title="GFI Latino"
            className="w-10 h-10 rounded-xl bg-brand-navy text-brand-cream text-xs font-black flex items-center justify-center shrink-0"
          >
            GFI
          </button>
        ) : (
          <BrandLogo />
        )}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          aria-expanded={!collapsed}
          className="flex items-center justify-center w-9 h-9 rounded-xl text-[var(--text-secondary)] hover:bg-[var(--bg-base)] hover:text-[var(--text-main)] transition-colors shrink-0"
        >
          <SafeIcon name={collapsed ? 'ChevronsRight' : 'ChevronsLeft'} className="w-5 h-5" />
        </button>
      </div>

      <nav className={`flex-1 py-4 space-y-1 overflow-y-auto hide-scrollbar ${collapsed ? 'px-2' : 'px-4'}`}>
        {sidebarNavItems.map((item) => (
          item.disabled ? (
            <div
              key={item.label}
              title={item.label}
              className={`flex items-center text-[var(--text-secondary)] cursor-not-allowed opacity-40 ${
                collapsed ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3'
              }`}
            >
              <SafeIcon name={item.icon} className="w-5 h-5 shrink-0" />
              {!collapsed && (
                <>
                  <span className="font-semibold text-sm">{item.label}</span>
                  <span className="ml-auto text-[8px] font-black px-1.5 py-0.5 bg-[var(--badge-bg)] rounded text-[var(--text-main)]">PRONTO</span>
                </>
              )}
            </div>
          ) : (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/app'}
              title={item.label}
              className={linkClass}
            >
              <SafeIcon name={item.icon} className="w-5 h-5 shrink-0" />
              {!collapsed && <span className="font-semibold text-sm">{item.label}</span>}
            </NavLink>
          )
        ))}

        {showAdmin && (
          <NavLink to="/app/admin/usuarios" title="Admin Usuarios" className={linkClass}>
            <SafeIcon name="Shield" className="w-5 h-5 shrink-0" />
            {!collapsed && <span className="font-semibold text-sm">Admin Usuarios</span>}
          </NavLink>
        )}

        {showSuperadmin && (
          <NavLink to="/app/admin/og-defaults" title="Vista Previa OG" className={linkClass}>
            <SafeIcon name="Share2" className="w-5 h-5 shrink-0" />
            {!collapsed && <span className="font-semibold text-sm">Vista Previa OG</span>}
          </NavLink>
        )}
      </nav>

      <div className={`space-y-2 border-t border-[var(--border-subtle)] ${collapsed ? 'p-2' : 'p-4'}`}>
        <button
          type="button"
          onClick={toggleTheme}
          title={isDark ? 'Modo Día' : 'Modo Noche'}
          className={`w-full flex items-center text-[var(--text-secondary)] hover:bg-[var(--bg-base)] rounded-xl transition-all ${
            collapsed ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3'
          }`}
        >
          <SafeIcon name={isDark ? 'Sun' : 'Moon'} className="w-5 h-5 shrink-0" />
          {!collapsed && (
            <span className="font-bold text-sm uppercase tracking-widest">
              {isDark ? 'Modo Día' : 'Modo Noche'}
            </span>
          )}
        </button>

        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate('/app/perfil')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/app/perfil');
            }
          }}
          title="Mi Perfil"
          className={`flex items-center text-[var(--text-secondary)] hover:bg-[var(--bg-base)] cursor-pointer rounded-xl transition-colors ${
            collapsed ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3'
          }`}
        >
          <div className="w-8 h-8 rounded-full bg-brand-navy border border-white/20 flex items-center justify-center text-brand-cream text-xs font-bold shrink-0">
            {displayName.charAt(0)}
          </div>
          {!collapsed && (
            <div className="flex-1 truncate">
              <p className="text-sm font-bold text-[var(--text-main)] truncate">Mi Perfil</p>
              <p className="text-[10px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">{displayRole}</p>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => signOut().then(() => navigate('/login'))}
          title="Salir"
          className={`w-full flex items-center text-[var(--text-secondary)] hover:bg-[var(--bg-base)] rounded-xl transition-all text-left ${
            collapsed ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3'
          }`}
        >
          <SafeIcon name="LogOut" className="w-5 h-5 shrink-0" />
          {!collapsed && <span className="font-bold text-sm uppercase tracking-widest">Salir</span>}
        </button>
      </div>
    </aside>
  );
}
